import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { appThemeRadius } from "styles/appThemeTokens";
import { FieldBlock, FieldHint } from "styles/formStyleComponents";

export { FieldBlock, FieldHint };

const uiFont = appThemeFonts.family.ui;

export const DrawerShell = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  backgroundColor: appThemeColors.background.paper,
  fontFamily: uiFont,
});

export const DrawerHeader = styled(Box)({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "18px 20px 14px",
  borderBottom: `1px solid ${appThemeColors.background.soft}`,
});

export const DrawerHeaderStacked = styled(DrawerHeader)({
  alignItems: "flex-start",
});

export const DrawerHeaderText = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 0,
});

export const DrawerTitle = styled(Typography)({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.xl,
  fontWeight: 700,
  color: appThemeColors.text.heading,
  margin: 0,
  lineHeight: 1.3,
});

export const DrawerCloseButton = styled(IconButton)({
  width: 30,
  height: 30,
  flex: "0 0 auto",
  borderRadius: appThemeRadius.md,
  backgroundColor: appThemeColors.background.main,
  color: appThemeColors.text.muted,
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
  },
  "&:focus-visible": {
    outline: "2px solid #0B5FA5",
    outlineOffset: "2px",
  },
});

export const DrawerCloseIcon = styled(CloseIcon)({
  fontSize: appThemeFonts.size.lgPlus,
});

export const DrawerBody = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 20,
});

export const DrawerDescription = styled(Typography)({
  fontSize: appThemeFonts.size.mdPlus,
  fontWeight: 400,
  color: appThemeColors.text.muted,
  margin: 0,
  lineHeight: 1.5,
});

export const DrawerFooter = styled(Box)({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 10,
  padding: "14px 20px",
  borderTop: `1px solid ${appThemeColors.background.soft}`,
});

export const CancelButton = styled(Button)({
  height: 38,
  padding: "0 18px",
  minWidth: 0,
  backgroundColor: "transparent",
  border: `1px solid ${appThemeColors.border.strong}`,
  borderRadius: appThemeRadius.pill,
  color: appThemeColors.text.lightBlue,
  fontSize: appThemeFonts.size.base,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
    borderColor: appThemeColors.text.lightBlue,
    color: appThemeColors.text.primary,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    opacity: 0.5,
  },
  "&.Mui-disabled:hover": {
    backgroundColor: "transparent",
    borderColor: appThemeColors.border.strong,
    color: appThemeColors.text.lightBlue,
  },
  "&:focus-visible": {
    outline: "2px solid #0B5FA5",
    outlineOffset: "2px",
  },
});

export const SubmitButton = styled(Button)({
  height: 38,
  padding: "0 22px",
  minWidth: 0,
  backgroundColor: appThemeColors.text.lightBlue,
  borderRadius: appThemeRadius.pill,
  color: appThemeColors.neutral.white,
  fontSize: appThemeFonts.size.base,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: appThemeColors.text.heading,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: appThemeColors.background.soft,
    color: appThemeColors.text.faint,
  },
  "&:focus-visible": {
    outline: "2px solid #0B5FA5",
    outlineOffset: "2px",
  },
});
